import { useEffect, useRef, useState } from "react";
import { MapPin, Search, BadgeCheck, PenLine, Loader2, Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Searchable travel-location picker with a never-blocked typed fallback.
// Layer 1: verified matches from the Super-Admin master (mode = flight/train/bus/hotel).
// Layer 2: if nothing matches, the user keeps their typed text as an UNVERIFIED query.
// A verified pick and a typed query are always visually distinct.
export const LocationPicker = ({ mode, value = "", onChange, placeholder = "Type a place…", testid = "location", allowRequest = false, near = null }) => {
  const [text, setText] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [popular, setPopular] = useState([]);
  const [nearby, setNearby] = useState([]);
  const boxRef = useRef(null);
  const tmr = useRef(null);
  const skipFocus = useRef(false);

  useEffect(() => { setText(value || ""); }, [value]);
  useEffect(() => { api.get(`/travel/locations/popular?mode=${mode}`).then((r) => setPopular(r.data.results || [])).catch(() => setPopular([])); }, [mode]);
  useEffect(() => {
    if (!near || !(near.state || near.city || near.district)) { setNearby([]); return; }
    const qs = new URLSearchParams({ mode });
    if (near.state) qs.set("state", near.state);
    if (near.city) qs.set("city", near.city);
    if (near.district) qs.set("district", near.district);
    api.get(`/travel/locations/nearest?${qs.toString()}`).then((r) => setNearby(r.data.results || [])).catch(() => setNearby([]));
  }, [mode, near?.state, near?.city, near?.district]);
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const runSearch = (qq) => {
    if (tmr.current) clearTimeout(tmr.current);
    tmr.current = setTimeout(() => {
      setLoading(true);
      api.get(`/travel/locations/search?mode=${mode}&q=${encodeURIComponent(qq)}`)
        .then((r) => setResults(r.data.results || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
  };

  const onType = (v) => {
    setText(v); setVerified(false); setOpen(true);
    onChange && onChange(v, { verified: false });
    runSearch(v);
  };
  const pickMaster = (loc) => {
    const disp = loc.code ? `${loc.name} (${loc.code})` : loc.name;
    if (tmr.current) clearTimeout(tmr.current);
    skipFocus.current = true;
    setText(disp); setVerified(true); setOpen(false); setResults([]);
    onChange && onChange(disp, { verified: true, location_id: loc.location_id, code: loc.code, city: loc.city });
    try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch (e) { /* noop */ }
  };
  const keepTyped = () => {
    if (tmr.current) clearTimeout(tmr.current);
    skipFocus.current = true;
    setVerified(false); setOpen(false); setResults([]);
    onChange && onChange(text, { verified: false });
    try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch (e) { /* noop */ }
  };
  const sendRequest = async () => {
    if (!text.trim()) return;
    try { await api.post("/travel/locations/request", { mode, text: text.trim() });
      toast.success("Sent to the team", { description: "We'll review and add this location if verified." });
    } catch (e) { if (e.response?.status === 401) toast.error("Please sign in to request a location."); else toast.error("Could not send request."); }
  };

  return (
    <div ref={boxRef} className="relative" data-testid={`${testid}-wrap`}>
      <div className={`flex items-center gap-1.5 rounded-md border px-2 ${verified ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"}`}>
        {verified ? <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-emerald-500" /> : <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
        <input value={text} onFocus={() => { if (skipFocus.current) { skipFocus.current = false; return; } setOpen(true); if (!results.length) runSearch(text); }} onChange={(e) => onType(e.target.value)} placeholder={placeholder} data-testid={testid} className="h-9 min-w-0 flex-1 bg-transparent text-sm outline-none" />
        {verified
          ? <span className="shrink-0 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700" data-testid={`${testid}-verified`}>VERIFIED</span>
          : text.trim() ? <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700" data-testid={`${testid}-typed`}>TYPED</span> : null}
      </div>
      {open && (
        <div className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-dropdown`}>
          {!text.trim() && nearby.length > 0 && (
            <div className="border-b border-slate-100 p-2" data-testid={`${testid}-nearby`}>
              <p className="mb-1.5 px-1 text-[10px] font-bold uppercase tracking-wide text-emerald-500">Near you</p>
              <div className="flex flex-wrap gap-1.5">
                {nearby.map((loc) => (
                  <button key={loc.location_id} type="button" onClick={() => pickMaster(loc)} data-testid={`${testid}-near-${loc.location_id}`} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700 hover:border-emerald-400">
                    <MapPin className="h-3 w-3 text-emerald-500" />{loc.code || loc.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!text.trim() && popular.length > 0 && (
            <div className="border-b border-slate-100 p-2" data-testid={`${testid}-popular`}>
              <p className="mb-1.5 px-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Popular</p>
              <div className="flex flex-wrap gap-1.5">
                {popular.map((loc) => (
                  <button key={loc.location_id} type="button" onClick={() => pickMaster(loc)} data-testid={`${testid}-pop-${loc.location_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:border-emerald-300 hover:bg-emerald-50">
                    <BadgeCheck className="h-3 w-3 text-emerald-500" />{loc.code || loc.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {loading && <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…</div>}
          {!loading && text.trim() && results.map((loc) => (
            <button key={loc.location_id} type="button" onClick={() => pickMaster(loc)} data-testid={`${testid}-opt-${loc.location_id}`} className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-emerald-50">
              <BadgeCheck className="h-4 w-4 shrink-0 text-emerald-500" />
              <span className="min-w-0 flex-1"><span className="text-sm font-semibold text-slate-800">{loc.name}</span>{loc.city && <span className="ml-1 text-xs text-slate-400">· {loc.city}</span>}</span>
              {loc.code && <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">{loc.code}</span>}
            </button>
          ))}
          {!loading && text.trim() && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500" data-testid={`${testid}-no-verified`}>No verified match for “{text.trim()}”.</div>
          )}
          {!loading && text.trim() && (
            <button type="button" onClick={keepTyped} data-testid={`${testid}-keep-typed`} className="flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2 text-left hover:bg-amber-50">
              <PenLine className="h-4 w-4 shrink-0 text-amber-500" />
              <span className="text-sm text-slate-700">Search for “<b>{text.trim()}</b>” <span className="text-[10px] font-bold text-amber-600">(typed / unverified)</span></span>
            </button>
          )}
          {!loading && allowRequest && text.trim() && results.length === 0 && (
            <button type="button" onClick={sendRequest} data-testid={`${testid}-request`} className="flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2 text-left text-sky-700 hover:bg-sky-50">
              <Plus className="h-4 w-4 shrink-0" /> <span className="text-sm">Request “{text.trim()}” be added to the master</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
